import React, { useEffect, useMemo, useState } from 'react';
import PlanDetailsItem from '../PlanDetails/PlanDetailsItem/PlanDetailsItem';
import ListItem from '../../../Utils/ListItem/ListItem';
import ProgressStatusBadge from '../../../Utils/ProgressStatusBadge/ProgressStatusBadge';
import { mapStatusToKey } from '../../../../Helpers/mapStatusToKey';
import { ImprovementDetail, ImproveTaskDetail } from '../../../../Types/Improve/ImprovementPlanDetail';
import CreationBar from '../../../Utils/Actions/CreationBar/CreationBar';
import MultiSelect, { ISelectOption } from '../../../Utils/MultiSelect/MultiSelect';
import DatePicker from '../../../Utils/DatePicker/DatePicker';
import DropDownList from '../../../Utils/DropDownList/DropDownList';
import CommentsIcon from '../../../Icons/CommentsIcon/CommentsIcon';
import Button from '../../../Utils/Button/Button';
import { axiosInstance } from '../../../../Authentication/AxiosInterceptor';

import EditableSidePane, { EditablePaneCtx } from '../../../Utils/EditableSidePane/EditableSidePane';
import {
  mapAssignedUsersToSelectOptionsFromAvailable,
  mapSelectOptionsToAssignedUsers,
  mapUsersToSelectOptions,
  UserDisplay
} from '../../../../Types/User';
import { mapSelectOptionsToStrings, mapStringsToSelectOptions } from '../../../Utils/MultiSelect/MultiSelectMethods';
import { mapStringsToDropdownItems } from '../../../Utils/MultiSelect/DropdownListMethods';
import { IDropDownListItem } from '../../../../Types/DropDownList/IDropDownListItem';
import Tooltip from '../../../Utils/Tooltip/Tooltip';
import { CheckNewImprovementVersion, UpdateImprovementByDetail } from '../../../../API/Improve/Improve';
import UrlField from '../../../Utils/URLField/UrlField';
import { ImproveTemplateDto } from '../../../../Types/Improve/ImproveTemplateDto';
import ConfirmAction from '../../../Utils/ConfirmAction/ConfirmAction';
import Separator from '../../../Utils/Separator/Separator';
import { isSetDate, MaybeDate, toDate, UNSET_DATE } from '../../../../Helpers/Dates';
import { formatImproveStatusLabel, getComputedTaskStatus } from '../../../../Helpers/Tasks';

type TasksPaneProps = {
  objective: ImprovementDetail;
  tasks: ImproveTaskDetail[] | null | undefined;
  assignedToFilterSubs: string[];
  activeTaskId?: string | null;
  onTaskClick?: (taskId: string) => void;
  setProgressNotesVisible: (visible: boolean) => void;
  onCreateTask: (title: string) => void;
  availableUsers: UserDisplay[];
  availableTags: string[];
  onObjectiveUpdated: (objective: ImprovementDetail) => void;
  setTaskCommentsVisible: (taskId: string) => void;
  editMode: boolean;
  roleName: string;
  saveObjectiveChanges: (objective: ImprovementDetail) => Promise<void>;
  template?: ImproveTemplateDto;
  /** Task ids with at least one comment — used to compute workflow status in the UI. */
  taskIdsWithComments?: ReadonlySet<string>;
};

const getParsedDate = (strDate: Date | string | undefined) => {
  if (!strDate) return 'N/A';
  const dateFormatter = new Intl.DateTimeFormat('en-GB', { dateStyle: 'full' });
  return dateFormatter.format(new Date(strDate));
};

const renderText = (value?: string | null) => (
  value?.trim() ? value : 'Not Set'
);

const objectiveTextAreaStyle = {
  width: '100%',
  boxSizing: 'border-box' as const,
  border: '1px solid var(--bs-gray-300)',
  fontSize: '0.8rem'
};

function ObjectiveDetailsFields({
  ctx,
  objective,
  availableUsers,
  userOptions,
  tagOptions,
  setProgressNotesVisible,
  template
}: {
  ctx: EditablePaneCtx<ImprovementDetail>;
  objective: ImprovementDetail; // used for "enabled" checks (API sends null when disabled)
  availableUsers: UserDisplay[];
  userOptions: ISelectOption[];
  tagOptions: ISelectOption[];
  setProgressNotesVisible: (visible: boolean) => void;
  template?: ImproveTemplateDto;
}) {
  const { isEditing, data, updateDraft } = ctx;

  // MultiSelect selected state (must react to draft changes)
  const [assignedSelected, setAssignedSelected] = useState<ISelectOption[]>([]);
  const [tagSelected, setTagSelected] = useState<ISelectOption[]>([]);

  useEffect(() => {
    setAssignedSelected(
      mapAssignedUsersToSelectOptionsFromAvailable(data.assignedTo, availableUsers)
    );
  }, [data.assignedTo, availableUsers]);

  useEffect(() => {
    setTagSelected(mapStringsToSelectOptions(data.tags));
  }, [data.tags]);

const impactOptions: IDropDownListItem[] = [
    { value: 'Not_Met', text: 'Not Met', colour: undefined, textColour: undefined },
    { value: 'Partially_Met', text: 'Partially Met', colour: undefined, textColour: undefined },
    { value: 'Met', text: 'Met', colour: undefined, textColour: undefined },
    { value: 'Exceeded', text: 'Exceeded', colour: undefined, textColour: undefined }
  ];

  // API sends null when not enabled, so only check != null
  const showAssignedTo = objective.assignedTo != null;
  const showTargetDate = objective.targetDate != null;
  const showCompletionDate = objective.completionDate != null;
  const showCustomDate = objective.customDate != null;

  const showExpectedImpact = objective.expectedImpact != null;
  const showActualImpact = objective.actualImpact != null;

  const showCustomTextA = objective.customTextA != null;
  const showCustomTextB = objective.customTextB != null;
  const showCustomTextC = objective.customTextC != null;

  const showUrl1 = objective.uRL1 != null;
  const showUrl2 = objective.uRL2 != null;
  const showUrl3 = objective.uRL3 != null;

  const showTags = objective.tags != null;

  return (
    <>
      {showAssignedTo && (
        <PlanDetailsItem text={`${template?.assignedTo?.name ?? "Assigned To"}:`} row={!isEditing}>
          {isEditing ? (
            <MultiSelect
              availableOptions={userOptions}
              selectedOptions={assignedSelected}
              onChange={(opts) => {
                setAssignedSelected(opts);
                updateDraft('assignedTo', mapSelectOptionsToAssignedUsers(opts, availableUsers) as any);
              }}
              placeholder="Assign users..."
            />
          ) : (
            <div>
              {data.assignedTo?.length ? data.assignedTo.map((u) => u.fullName).join(', ') : 'Not Set'}
            </div>
          )}
        </PlanDetailsItem>
      )}

      {showTargetDate && (
        <PlanDetailsItem text={`${template?.targetDate?.name ?? "Target Date"}:`} row>
          {isEditing ? (
            <DatePicker value={data.targetDate} onChange={(v) => updateDraft('targetDate', v as any)} />
          ) : (
            <div>
              {getParsedDate(data.targetDate)}
            </div>
          )}
        </PlanDetailsItem>
      )}

      {showCompletionDate && (
        <PlanDetailsItem text={`${template?.completionDate?.name ?? "Completion Date"}:`} row>
          {isEditing ? (
            <DatePicker value={data.completionDate} onChange={(v) => updateDraft('completionDate', v as any)} />
          ) : (
            <div>
              {getParsedDate(data.completionDate)}
            </div>
          )}
        </PlanDetailsItem>
      )}

      {showCustomDate && (
        <PlanDetailsItem text={`${template?.customDateA?.name ?? "Custom Date A"}:`} row>
          {isEditing ? (
            <DatePicker value={data.customDate} onChange={(v) => updateDraft('customDate', v as any)} />
          ) : (
            <div>
              {getParsedDate(data.customDate)}
            </div>
          )}
        </PlanDetailsItem>
      )}

      {showExpectedImpact && (
        <PlanDetailsItem text={`${template?.expectedImpact?.name ?? "Expected Impact"}:`} column={isEditing}>
          {isEditing ? (
            <textarea
              style={objectiveTextAreaStyle}
              value={(data.expectedImpact ?? '') as any}
              onChange={(e) => updateDraft('expectedImpact', e.target.value as any)}
            />
          ) : (
            renderText(data.expectedImpact as any)
          )}
        </PlanDetailsItem>
      )}

      {/* If you want this always visible, don’t gate it with the API flag */}
      <PlanDetailsItem text="Progress Notes:" row>
        <Button
          clickHandler={() => setProgressNotesVisible(true)}
          colour="#FFF"
          textColour={"#000"}
          border="rgba(0,0,0,0.2)"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: 4, marginLeft: '3px' }}
        >
          <span style={{ fontSize: '0.6875rem' }}>View Notes</span>
          <CommentsIcon size={18} />
        </Button>
      </PlanDetailsItem>

      {showActualImpact && (
        <PlanDetailsItem text={`${template?.actualImpact?.name ?? "Actual Impact"}:`} row={!isEditing}>
          {isEditing ? (
            <DropDownList
              value={(data.actualImpact ?? '') as any}
              placeholder="Select impact..."
              data={impactOptions}
              clearable
              onChange={(item) => {
                updateDraft('actualImpact', item.value as any);
              }}
            />
          ) : (
            renderText(data.actualImpact ? String(data.actualImpact).replace(/_/g, ' ') : null)
          )}
        </PlanDetailsItem>
      )}

      {showTags && (
        <PlanDetailsItem text={`${template?.tags?.name ?? "Tags"}:`}  row={!isEditing}>
          {isEditing ? (
            <MultiSelect
              availableOptions={tagOptions}
              selectedOptions={tagSelected}
              onChange={(opts) => {
                setTagSelected(opts);
                updateDraft('tags', mapSelectOptionsToStrings(opts) as any);
              }}
              placeholder="Select tags..."
            />
          ) : (
            <div>
              {data.tags?.length ? data.tags.join(', ') : 'Not Set'}
            </div>
          )}
        </PlanDetailsItem>
      )}

      {showCustomTextA && (
        <PlanDetailsItem text={`${template?.columnA?.name ?? "Column A"}:`} column={isEditing}>
          {isEditing ? (
            <textarea
              style={objectiveTextAreaStyle}
              value={(data.customTextA ?? '') as any}
              onChange={(e) => updateDraft('customTextA', e.target.value as any)}
            />
          ) : (
            renderText(data.customTextA as any)
          )}
        </PlanDetailsItem>
      )}

      {showCustomTextB && (
        <PlanDetailsItem text={`${template?.columnB?.name ?? "Column B"}:`} column={isEditing}>
          {isEditing ? (
            <textarea
              style={objectiveTextAreaStyle}
              value={(data.customTextB ?? '') as any}
              onChange={(e) => updateDraft('customTextB', e.target.value as any)}
            />
          ) : (
            renderText(data.customTextB as any)
          )}
        </PlanDetailsItem>
      )}

      {showCustomTextC && (
        <PlanDetailsItem text={`${template?.columnC?.name ?? "Column C"}:`} column={isEditing}>
          {isEditing ? (
            <textarea
              style={objectiveTextAreaStyle}
              value={(data.customTextC ?? '') as any}
              onChange={(e) => updateDraft('customTextC', e.target.value as any)}
            />
          ) : (
            renderText(data.customTextC as any)
          )}
        </PlanDetailsItem>
      )}

      {showUrl1 && (
        <PlanDetailsItem text={`${template?.url1?.name ?? "URL 1"}:`} column={isEditing} row={!isEditing}>
          <UrlField url={data.uRL1 ?? ''} text={data.uRL1Text ?? ''} urlChanged={(newUrl) => updateDraft('uRL1', newUrl)} textChanged={(newText) => updateDraft('uRL1Text', newText)} disabled={!isEditing} />
        </PlanDetailsItem>
      )}

      {showUrl2 && (
        <PlanDetailsItem text={`${template?.url2?.name ?? "URL 2"}:`} column={isEditing} row={!isEditing}>
          <UrlField url={data.uRL2 ?? ''} text={data.uRL2Text ?? ''} urlChanged={(newUrl) => updateDraft('uRL2', newUrl)} textChanged={(newText) => updateDraft('uRL2Text', newText)} disabled={!isEditing} />
        </PlanDetailsItem>
      )}

      {showUrl3 && (
        <PlanDetailsItem text={`${template?.url3?.name ?? "URL 3"}:`} column={isEditing} row={!isEditing}>
          <UrlField url={data.uRL3 ?? ''} text={data.uRL3Text ?? ''} urlChanged={(newUrl) => updateDraft('uRL3', newUrl)} textChanged={(newText) => updateDraft('uRL3Text', newText)} disabled={!isEditing} />
        </PlanDetailsItem>
      )}
    </>
  );
}

const TasksPane = ({
  objective,
  tasks,
  assignedToFilterSubs,
  activeTaskId,
  onTaskClick,
  setProgressNotesVisible,
  onCreateTask,
  availableUsers,
  availableTags,
  onObjectiveUpdated,
  saveObjectiveChanges,
  setTaskCommentsVisible,
  editMode,
  roleName,
  template,
  taskIdsWithComments,
}: TasksPaneProps) => {
  const userOptions = useMemo(() => mapUsersToSelectOptions(availableUsers), [availableUsers]);
  const tagOptions = useMemo(() => mapStringsToSelectOptions(availableTags), [availableTags]);

  const [pendingItem, setPendingItem] = useState<string>();

  const checkNewVersion = async (data: ImprovementDetail) => {
    console.log('Old version:', objective, 'New version draft:', data);
    const latestImprovement = await CheckNewImprovementVersion(objective.id, objective.version);
    if (latestImprovement) {
      onObjectiveUpdated(latestImprovement);
    }
  }

  useEffect(() => {
    checkNewVersion(objective);
  }, [objective.id]);

  const taskCommentsVisibleHandler = (taskId: string) => {
    setTaskCommentsVisible(taskId);
  }

  const deleteTaskHandler = async () => {
    if (!pendingItem) return;
    
    await axiosInstance.get(`Improve/DeleteImproveTask/${pendingItem}`);
    
    onObjectiveUpdated({
      ...objective,
      tasks: objective.tasks?.filter(t => t.uniqueId !== pendingItem) || []
    });

    setPendingItem(undefined);
  }

  const showDeleteConfirm = (taskId: string) => {
    setPendingItem(taskId);
  }

  const { matchingTasks, nonMatchingTasks } = useMemo(() => {
    const selectedSubs = assignedToFilterSubs;
    const allTasks = tasks ?? [];

    if (selectedSubs.length === 0) {
      return { matchingTasks: allTasks, nonMatchingTasks: [] as ImproveTaskDetail[] };
    }

    const matching: ImproveTaskDetail[] = [];
    const nonMatching: ImproveTaskDetail[] = [];

    for (const task of allTasks) {
      if (task.assignedTo?.sub && selectedSubs.includes(task.assignedTo.sub)) {
        matching.push(task);
      } else {
        nonMatching.push(task);
      }
    }

    return { matchingTasks: matching, nonMatchingTasks: nonMatching };
  }, [tasks, assignedToFilterSubs]);

  const filteredUserLabel = useMemo(() => {
    if (assignedToFilterSubs.length === 0) return '';
    const names = assignedToFilterSubs.map(sub =>
      availableUsers.find(u => u.sub === sub)?.fullName ?? sub
    );
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} or ${names[1]}`;
    return `${names[0]} +${names.length - 1} more`;
  }, [assignedToFilterSubs, availableUsers]);

  // API sends null when not enabled
  const showDetails =
    objective.assignedTo != null ||
    objective.targetDate != null ||
    objective.completionDate != null ||
    objective.customDate != null ||
    objective.expectedImpact != null ||
    objective.actualImpact != null ||
    objective.customTextA != null ||    objective.customTextB != null ||
    objective.customTextC != null ||
    objective.tags != null;

  const footer = (
    <div style={{borderTop: '1px solid #e5e7eb'}}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', padding: '1rem 0', borderBottom: '1px solid #f3f4f6' }}>
        <h4 style={{ fontSize: '1rem', fontWeight: 400, fontFamily: 'inherit', color: '#6c757d', textTransform: 'uppercase', margin: 0, paddingLeft: '1.25rem' }}>Tasks</h4>
      </div>

      <ul style={{ listStyle: 'none', padding: '0.75rem', margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {onCreateTask && (
          <Tooltip text={`Your role as ${roleName} means that you cannot create tasks`} align={'top'} sideAlign='right' disabled={editMode}>
            <CreationBar
              onCreateHandler={onCreateTask}
              placeholder="Enter task title..."
              noTextMessage="Please enter a task title"
              disabled={!editMode}
            />
          </Tooltip>
        )}

        {assignedToFilterSubs.length > 0 && matchingTasks.length > 0 && (
          <li style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.75rem', padding: '0.25rem 0' }}>
            Assigned to {filteredUserLabel}
          </li>
        )}

        {matchingTasks.map((t) => {
          const isActive = activeTaskId === t.uniqueId;
          const status = getComputedTaskStatus(t, {
            hasComments: taskIdsWithComments?.has(t.uniqueId),
          });

          return (
            <ListItem key={t.uniqueId} active={isActive}>
              <div onClick={() => onTaskClick?.(t.uniqueId)} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '0.5rem'}}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <ProgressStatusBadge status={mapStatusToKey(status)} text={formatImproveStatusLabel(status)} style={{ borderRadius: "0.3rem" }}/>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {editMode && 
                      <Button
                        clickHandler={() => showDeleteConfirm(t.uniqueId)}
                        colour={'rgb(240, 223, 222)'}
                        textColour={"black"}
                      >
                        <img src="/img/trash-red.svg" alt="Delete Task" style={{ width: '16px', height: '16px' }} /> 
                      </Button>
                    }
                    
                    <Button
                      clickHandler={() => setTaskCommentsVisible(t.uniqueId)}
                      colour={isActive ? 'transparent' : '#FFF'}
                    >
                      <CommentsIcon size={18} />
                    </Button>
                  </div>
                 
                </div>
                  <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 400, fontFamily: 'inherit' }}>{t.taskHeading}</h3>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      // extra safety: prevents focus/click quirks in some button implementations
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    style={{ display: 'flex' }}
                  >
                </div>
              </div>
            </ListItem>
          );
        })}

        {assignedToFilterSubs.length > 0 && matchingTasks.length > 0 && nonMatchingTasks.length > 0 && (
          <li style={{ listStyle: 'none' }}>
            <Separator text="No matches found" />
          </li>
        )}

        {nonMatchingTasks.map((t) => {
          const isActive = activeTaskId === t.uniqueId;
          const status = getComputedTaskStatus(t, {
            hasComments: taskIdsWithComments?.has(t.uniqueId),
          });

          return (
            <ListItem key={t.uniqueId} active={isActive}>
              <div onClick={() => onTaskClick?.(t.uniqueId)} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '0.5rem', opacity: 0.45 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <ProgressStatusBadge status={mapStatusToKey(status)} text={formatImproveStatusLabel(status)} style={{ borderRadius: "0.3rem" }}/>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {editMode &&
                      <Button
                        clickHandler={() => showDeleteConfirm(t.uniqueId)}
                        colour={'rgb(240, 223, 222)'}
                        textColour={"black"}
                      >
                        <img src="/img/trash-red.svg" alt="Delete Task" style={{ width: '16px', height: '16px' }} />
                      </Button>
                    }

                    <Button
                      clickHandler={() => setTaskCommentsVisible(t.uniqueId)}
                      colour={isActive ? 'transparent' : '#FFF'}
                    >
                      <CommentsIcon size={18} />
                    </Button>
                  </div>
                </div>
                <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 400, fontFamily: 'inherit' }}>{t.taskHeading}</h3>
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  style={{ display: 'flex' }}
                >
                </div>
              </div>
            </ListItem>
          );
        })}
        </ul>

        {pendingItem && (
          <ConfirmAction
            isVisible={pendingItem !== undefined}
            onClose={() => {
              setPendingItem(undefined);
            }}
            clickHandler={deleteTaskHandler}
            title={`Delete Task`}
            message={`Are you sure you want to delete this task? This action cannot be undone.`}
            severity={"danger"}
            icon={"/img/trash-red.svg"}
          />
      )}
    </div>
  );

  return (
    <EditableSidePane<ImprovementDetail>
      entity={objective}
      entityId={objective.id}
      onSave={saveObjectiveChanges}
      onUpdated={onObjectiveUpdated}
      showDetails={showDetails}
      editText="Edit Objective"
      doneText="Finished Editing"
      titleKey="title"
      titleFallback="Untitled Objective"
      renderDetails={(ctx) => (
        <ObjectiveDetailsFields
          ctx={ctx}
          objective={objective}
          availableUsers={availableUsers}
          userOptions={userOptions}
          tagOptions={tagOptions}
          setProgressNotesVisible={setProgressNotesVisible}
          template={template}
        />
      )}
      footer={footer}
      editMode={editMode}
      locked={objective.lockedBy != null}
      lockedBy={objective.lockedBy?.fullName}
      onContentClick={() => {}}
    />
  );
};

export default TasksPane;
